import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Megaphone, ChartBar, Timer, ArrowRight, Camera, Warning, DeviceMobile } from "@phosphor-icons/react";

function liveCountdown(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return null;
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

export default function ElectionsPreview({ theme = "dark" }) {
    const [els, setEls] = useState(null);
    const [, setTick] = useState(0);
    // undefined = still probing, true = camera present, false = not present.
    // We only run the probe when at least one active secure election exists.
    const [hasCamera, setHasCamera] = useState(undefined);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/elections");
                if (!cancelled) setEls(data || []);
            } catch {
                if (!cancelled) setEls([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Detect if the current device has a video input. We only run the check
    // when there's actually an active secure election — no need to prompt or
    // probe voters who aren't about to hit a camera-required ballot.
    useEffect(() => {
        if (!Array.isArray(els)) return;
        const anySecureActive = els.some((e) => !e.is_over && e.secure_election);
        if (!anySecureActive) { setHasCamera(undefined); return; }
        let cancelled = false;
        (async () => {
            try {
                if (!navigator.mediaDevices?.enumerateDevices) {
                    if (!cancelled) setHasCamera(false);
                    return;
                }
                const devs = await navigator.mediaDevices.enumerateDevices();
                if (!cancelled) setHasCamera(devs.some((d) => d.kind === "videoinput"));
            } catch {
                if (!cancelled) setHasCamera(false);
            }
        })();
        return () => { cancelled = true; };
    }, [els]);

    useEffect(() => {
        const t = setInterval(() => setTick((x) => x + 1), 60_000);
        return () => clearInterval(t);
    }, []);

    if (els === null) {
        return <div className={`hash-mono text-xs ${theme === "light" ? "text-slate-500" : "text-civic-200/60"}`}>loading active elections…</div>;
    }

    const active = els.filter((e) => !e.is_over);
    const closed = els.filter((e) => e.is_over).slice(0, 3);
    const activeSecure = active.filter((e) => e.secure_election);
    // Show the warning only after the probe has resolved and returned "no camera".
    const showCameraWarning = hasCamera === false && activeSecure.length > 0;
    const light = theme === "light";

    return (
        <div className="space-y-3">
            {showCameraWarning && (
                <div
                    className={`rounded-md border-2 px-3 py-2.5 flex items-start gap-2 ${
                        light
                            ? "border-rose-300 bg-rose-50 text-rose-900"
                            : "border-rose-400/60 bg-rose-500/15 text-rose-100"
                    }`}
                    role="alert"
                    data-testid="login-no-camera-warning"
                >
                    <div className={`shrink-0 h-7 w-7 grid place-items-center rounded-full ${light ? "bg-rose-100 border border-rose-200" : "bg-white/15 border border-white/25"}`}>
                        <Warning size={15} weight="fill" className={light ? "text-rose-600" : "text-white"} />
                    </div>
                    <div className="min-w-0 text-[12px] leading-snug">
                        <div className={`font-bold uppercase tracking-wider text-[10px] mb-0.5 ${light ? "text-rose-700" : "text-rose-200"}`}>
                            No camera detected on this device
                        </div>
                        <div>
                            {activeSecure.length === 1
                                ? <><span className="font-semibold">{activeSecure[0].title}</span> is a secure election and needs a camera to submit your ballot.</>
                                : <><span className="font-semibold">{activeSecure.length} active elections</span> are secure and need a camera to submit your ballot.</>
                            }{" "}
                            Sign in from a phone, tablet, or laptop with a working camera to cast your vote.
                        </div>
                        <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${light ? "bg-rose-100 border border-rose-200" : "bg-white/10 border border-white/20"}`}>
                            <DeviceMobile size={11} weight="bold" /> Switch to a device with a camera
                        </div>
                    </div>
                </div>
            )}
            <Section theme={theme} title={`Active Elections${active.length ? ` (${active.length})` : ""}`} icon={Megaphone} accent>
                {active.length === 0 ? (
                    <div className={`text-xs italic ${theme === "light" ? "text-slate-500" : "text-civic-200/70"}`}>No active elections at this time.</div>
                ) : (
                    <div className="space-y-1.5 max-w-2xl" data-testid="login-elections-active">
                        {active.map((e) => <PreviewCard key={e.id} e={e} prominent />)}
                    </div>
                )}
            </Section>

            {closed.length > 0 && (
                <Section theme={theme} title={`Recently Closed (${closed.length})`} icon={ChartBar}>
                    <div className="space-y-1.5 max-w-2xl" data-testid="login-elections-closed">
                        {closed.map((e) => (
                            <Link key={e.id} to={`/results/${e.id}`} className="block group" data-testid={`preview-results-link-${e.id}`}>
                                <PreviewCard e={e} clickable />
                            </Link>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}

function Section({ title, icon: Icon, accent = false, children, theme = "dark" }) {
    const light = theme === "light";
    return (
        <section>
            <div className="flex items-center gap-1.5 mb-1.5">
                <div className={`h-5 w-5 grid place-items-center rounded ${
                    accent
                        ? (light ? "bg-emerald-100 border border-emerald-200" : "bg-emerald-500/20 border border-emerald-400/40")
                        : (light ? "bg-slate-100 border border-slate-200" : "bg-white/10 border border-white/20")
                }`}>
                    <Icon size={11} weight="duotone" className={
                        accent
                            ? (light ? "text-emerald-700" : "text-emerald-200")
                            : (light ? "text-slate-600" : "text-civic-100")
                    } />
                </div>
                <h3 className={`font-heading font-bold text-xs uppercase tracking-wider ${light ? "text-slate-700" : "text-white"}`}>{title}</h3>
            </div>
            {children}
        </section>
    );
}

function PreviewCard({ e, clickable = false, prominent = false }) {
    const now = Date.now();
    const startsMs = e.starts_at ? new Date(e.starts_at).getTime() : null;
    const notStarted = !e.is_over && startsMs && startsMs > now;
    const untilStart = notStarted ? liveCountdown(e.starts_at) : null;
    const untilEnd   = notStarted ? null : liveCountdown(e.ends_at);

    let stamp;
    if (e.is_over) {
        stamp = (
            <span className="inline-flex items-center gap-1 text-slate-700 font-semibold hash-mono shrink-0">
                <Timer size={prominent ? 13 : 11} weight="fill" /> Closed
            </span>
        );
    } else if (notStarted) {
        stamp = (
            <span className={`inline-flex items-center gap-1 text-amber-700 font-semibold hash-mono shrink-0 ${prominent ? "text-sm" : ""}`} title={`Polls open ${e.starts_at ? new Date(e.starts_at).toLocaleString() : "soon"}`}>
                <Timer size={prominent ? 13 : 11} weight="fill" /> Opens in {untilStart || "soon"}
            </span>
        );
    } else if (untilEnd) {
        stamp = (
            <span className={`inline-flex items-center gap-1 text-emerald-700 font-semibold hash-mono shrink-0 ${prominent ? "text-sm" : ""}`}>
                <Timer size={prominent ? 13 : 11} weight="fill" /> {untilEnd}
            </span>
        );
    } else {
        stamp = (
            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold hash-mono shrink-0">
                <Timer size={prominent ? 13 : 11} weight="fill" /> Opening soon
            </span>
        );
    }

    const cardCls = prominent
        ? "bg-white border-2 border-emerald-300 ring-1 ring-emerald-100 shadow-sm px-4 py-3 text-slate-900 flex items-center gap-3"
        : "bg-white border border-slate-200 sm:border-0 px-3 py-2 sm:py-1.5 text-slate-900 flex items-center gap-3";
    const textCls = prominent
        ? "flex-1 min-w-0 flex flex-wrap sm:flex-nowrap items-center gap-x-2.5 gap-y-0.5 text-[13px] leading-tight"
        : "flex-1 min-w-0 flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-0.5 text-[11px] leading-tight";
    const titleCls = prominent
        ? "font-heading font-bold text-lg text-slate-900 truncate max-w-full"
        : "font-heading font-bold text-sm text-slate-900 truncate max-w-full";
    const badgeCls = prominent
        ? "shrink-0 px-2.5 py-1.5 rounded bg-emerald-600 text-white flex items-center gap-1.5"
        : "shrink-0 px-1.5 py-0.5 rounded bg-civic-600 text-white flex items-center gap-1";
    const badgeNumCls = prominent
        ? "font-heading font-black text-lg leading-none"
        : "font-heading font-black text-xs leading-none";
    const badgeLblCls = prominent
        ? "text-[9px] uppercase tracking-wider opacity-90"
        : "text-[8px] uppercase tracking-wider opacity-90";

    return (
        <Card className={cardCls} data-testid={`preview-election-${e.id}`}>
            <div className={textCls}>
                <span className={titleCls} data-testid={`preview-authority-${e.id}`}>
                    {e.authority || "—"}
                </span>
                <span className="text-slate-300 shrink-0 hidden sm:inline">·</span>
                <span className="text-slate-600 truncate max-w-full" data-testid={`preview-title-${e.id}`}>{e.title}</span>
                {e.secure_election && (
                    <span
                        className={`shrink-0 inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${
                            prominent
                                ? "text-[10px] bg-rose-50 text-rose-700 border-rose-200 px-2 py-0.5"
                                : "text-[9px] bg-rose-50 text-rose-700 border-rose-200 px-1.5 py-0.5"
                        }`}
                        title="Secure election — voter photo required at submission"
                        data-testid={`preview-secure-${e.id}`}
                    >
                        <Camera size={prominent ? 11 : 9} weight="fill" /> Secure
                    </span>
                )}
                <span className="text-slate-300 shrink-0 hidden sm:inline">·</span>
                {stamp}
            </div>
            <div className={badgeCls} data-testid={`preview-items-${e.id}`}>
                <span className={badgeNumCls}>{e.items_count ?? 0}</span>
                <span className={badgeLblCls}>Items</span>
            </div>
        </Card>
    );
}
